import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Re-fetches the given query keys on an interval while the screen is mounted.
 *
 * The inquiry screens are a live conversation between two people who are not in the same
 * room, and neither screen refreshed itself: a coordinator watching a thread for an answer,
 * or a secretary watching one for a reply, saw nothing until they reloaded the page by hand.
 * There is no push channel to use instead - nothing in this codebase speaks SignalR.
 *
 * Keys are prefix-matched, so pass the url a useGetAuthApi call was made with; that hook
 * builds its key as [url, ...] and invalidation by [url] reaches it.
 *
 * Empty or blank keys are ignored, which is what a page passes while nothing is selected.
 */
export default function useQueryRefreshInterval(keys: string[], intervalMs: number): void {
  const queryClient = useQueryClient();

  /*
   * Joined, so a caller can pass a fresh array every render - which every caller does -
   * without restarting the interval on each one.
   */
  const keysSignature = keys.filter(Boolean).join('|');

  useEffect(() => {
    const activeKeys = keysSignature.split('|').filter(Boolean);
    if (activeKeys.length === 0) return;

    const interval = setInterval(() => {
      // Only while the tab is visible: a background tab does not need a poll per screen.
      if (document.hidden) return;

      activeKeys.forEach((key) => {
        /*
         * ⚠️ Skip a key whose request is still in the air. Invalidating mid-flight makes
         * react-query abort the previous request and start another, and the abort surfaces as
         * a red "CanceledError: canceled" in the console via the app's own request logging.
         * On a slow response that happened on every tick, which filled the console with
         * errors that looked like a failure and were not one.
         */
        const isFetching = queryClient.isFetching({ queryKey: [key] }) > 0;
        if (isFetching) return;

        queryClient.invalidateQueries({ queryKey: [key] });
      });
    }, intervalMs);

    return () => clearInterval(interval);
  }, [keysSignature, intervalMs, queryClient]);
}
